import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

interface LogoData {
  logo_header: string;
  logo_footer: string;
  site_name: string;
}

const defaults: LogoData = {
  logo_header: '',
  logo_footer: '',
  site_name: 'Ma Gádeni',
};

export default function AdminLogoPage() {
  const [data, setData] = useState<LogoData>(defaults);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => { loadLogo(); }, []);

  const loadLogo = async () => {
    const { data: rows } = await supabase
      .from('site_settings')
      .select('key, value')
      .in('key', ['logo_header', 'logo_footer', 'site_name']);

    if (rows) {
      const map: Record<string, string> = {};
      rows.forEach(r => { map[r.key] = r.value || ''; });
      setData({ logo_header: map.logo_header || '', logo_footer: map.logo_footer || '', site_name: map.site_name || 'Ma Gádeni' });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    const updates = [
      { key: 'logo_header', value: data.logo_header },
      { key: 'logo_footer', value: data.logo_footer },
      { key: 'site_name', value: data.site_name },
    ];
    for (const u of updates) {
      const { data: existing } = await supabase.from('site_settings').select('id').eq('key', u.key).maybeSingle();
      if (existing) {
        await supabase.from('site_settings').update({ value: u.value, updated_at: new Date().toISOString() }).eq('key', u.key);
      } else {
        await supabase.from('site_settings').insert({ key: u.key, value: u.value });
      }
    }
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const inputCls = "w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-[#C9A84C] transition-colors";

  const LogoPreview = ({ url, label }: { url: string; label: string }) => (
    <div className="border border-gray-100 rounded-lg p-4">
      <p className="text-xs text-gray-400 mb-3">{label}</p>
      <div className="h-16 flex items-center justify-center bg-[#0d0d0d] rounded-lg">
        {url ? (
          <img src={url} alt={label} className="max-h-12 max-w-full object-contain" />
        ) : (
          <span className="text-white/30 text-sm font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {data.site_name}
          </span>
        )}
      </div>
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-800">Logomarca</h1>
          <p className="text-gray-400 text-sm mt-0.5">Gerencie a identidade visual do site</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#C9A84C] hover:bg-[#D4AF37] text-black text-sm font-semibold px-5 py-2.5 rounded-lg whitespace-nowrap cursor-pointer transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          {saving ? <><i className="ri-loader-4-line animate-spin"></i> Salvando...</> : saved ? <><i className="ri-check-line"></i> Salvo!</> : <><i className="ri-save-line"></i> Salvar</>}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-5">
          <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4">
            <h2 className="text-sm font-semibold text-gray-700 border-b border-gray-100 pb-3">Nome do Site</h2>
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5">Nome exibido quando não há logo</label>
              <input className={inputCls} value={data.site_name} onChange={e => setData({ ...data, site_name: e.target.value })} />
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4">
            <h2 className="text-sm font-semibold text-gray-700 border-b border-gray-100 pb-3">Logo do Header</h2>
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5">URL da imagem (PNG, SVG, WebP)</label>
              <input className={inputCls} value={data.logo_header} onChange={e => setData({ ...data, logo_header: e.target.value })} placeholder="https://..." />
            </div>
            <p className="text-xs text-gray-400">Cole a URL de uma imagem hospedada. Recomendado: fundo transparente, altura máxima 60px.</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4">
            <h2 className="text-sm font-semibold text-gray-700 border-b border-gray-100 pb-3">Logo do Footer</h2>
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5">URL da imagem (PNG, SVG, WebP)</label>
              <input className={inputCls} value={data.logo_footer} onChange={e => setData({ ...data, logo_footer: e.target.value })} placeholder="https://..." />
            </div>
            <p className="text-xs text-gray-400">Versão clara ou dourada da logo para fundo escuro do footer.</p>
          </div>
        </div>

        <div className="space-y-5">
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <h2 className="text-sm font-semibold text-gray-700 border-b border-gray-100 pb-3 mb-4">Pré-visualização</h2>
            <div className="space-y-4">
              <LogoPreview url={data.logo_header} label="Header (fundo escuro)" />
              <LogoPreview url={data.logo_footer} label="Footer (fundo escuro)" />
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
            <div className="flex gap-3">
              <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                <i className="ri-lightbulb-line text-amber-500 text-sm"></i>
              </div>
              <div>
                <p className="text-sm font-medium text-amber-800">Dica de upload</p>
                <p className="text-xs text-amber-600 mt-1">
                  Use a <strong>Biblioteca de Mídia</strong> para fazer upload da sua logo e copiar a URL gerada. Depois cole aqui nos campos acima.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
